import { useCallback, useEffect, useState } from "react";
import { useAlertContext } from "../../context/AlertContext";
import {
    createAsset,
    createAssetCategory,
    getAssetCategories,
    getAssetHistory,
    getEmployeeAssets,
    getFreeAssets,
    moveAsset,
} from "../../services/assetsApi";
import { AssetHistoryList, assetTitle, identifierLabel, KIND_LABELS, MoveAssetForm, sinceText } from "./assetParts";

/**
 * Вкладка «Техника» карточки сотрудника: что за ним числится (техника, SIM с номером, аккаунты),
 * выдать новое или свободное, передать другому / отделу / рабочему месту, вернуть, история.
 * Ведут HR и бухгалтерия. Чат 4, правка №15 (MR 3). [Claude] 2026-11-02
 */
const inputCls = "rounded-lg border border-slate-200 px-3 py-2 text-sm";
const emptyNew = { category_id: "", inventory_number: "", identifier: "", note: "" };

export default function EmployeeAssetsTab({ employeeId, employee, canManage = false }) {
    const { showAlert } = useAlertContext();
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState(null); // "new" | "free" | null
    const [form, setForm] = useState(emptyNew);
    const [freeItems, setFreeItems] = useState([]);
    const [freeId, setFreeId] = useState("");
    const [newCategory, setNewCategory] = useState(null);
    const [moving, setMoving] = useState(null);
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState({});

    const fired = Boolean(employee?.date_fired);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [rows, cats] = await Promise.all([getEmployeeAssets(employeeId), getAssetCategories()]);
            setItems(Array.isArray(rows) ? rows : []);
            setCategories(Array.isArray(cats) ? cats : []);
        } catch (error) {
            showAlert(error.message || "Не удалось загрузить технику", "error");
        } finally {
            setLoading(false);
        }
    }, [employeeId, showAlert]);

    useEffect(() => { load(); }, [load]);

    const category = categories.find((c) => String(c.id) === String(form.category_id));

    const openFree = async () => {
        setMode("free");
        try {
            const rows = await getFreeAssets();
            setFreeItems(Array.isArray(rows) ? rows : []);
        } catch (error) {
            showAlert(error.message || "Не удалось загрузить свободное", "error");
        }
    };

    const issueNew = async (event) => {
        event.preventDefault();
        if (!form.category_id) { showAlert("Выберите категорию", "error"); return; }
        setBusy(true);
        try {
            await createAsset({
                category_id: Number(form.category_id),
                inventory_number: form.inventory_number.trim() || null,
                identifier: form.identifier.trim() || null,
                note: form.note.trim() || null,
                to: { type: "employee", employee_id: Number(employeeId) },
            });
            showAlert("Выдано", "success");
            setForm(emptyNew);
            setMode(null);
            load();
        } catch (error) {
            showAlert(error.message || "Не удалось выдать", "error");
        } finally {
            setBusy(false);
        }
    };

    const issueFree = async (event) => {
        event.preventDefault();
        if (!freeId) { showAlert("Выберите, что выдать", "error"); return; }
        setBusy(true);
        try {
            await moveAsset(Number(freeId), { type: "employee", employee_id: Number(employeeId) });
            showAlert("Выдано", "success");
            setFreeId("");
            setMode(null);
            load();
        } catch (error) {
            showAlert(error.message || "Не удалось выдать", "error");
        } finally {
            setBusy(false);
        }
    };

    const addCategory = async (event) => {
        event.preventDefault();
        if (!newCategory?.name?.trim()) return;
        try {
            const row = await createAssetCategory({ name: newCategory.name.trim(), kind: newCategory.kind || "device" });
            setCategories((list) => [...list, row]);
            setForm((v) => ({ ...v, category_id: String(row.id) }));
            setNewCategory(null);
        } catch (error) {
            showAlert(error.message || "Не удалось добавить категорию", "error");
        }
    };

    const move = async (asset, body) => {
        setBusy(true);
        try {
            await moveAsset(asset.id, body);
            showAlert(body.type === "free" ? "Возвращено — теперь свободно" : "Передано", "success");
            setMoving(null);
            setHistory((h) => { const next = { ...h }; delete next[asset.id]; return next; });
            load();
        } catch (error) {
            showAlert(error.message || "Не удалось передать", "error");
        } finally {
            setBusy(false);
        }
    };

    const toggleHistory = async (asset) => {
        if (history[asset.id]) {
            setHistory((h) => { const next = { ...h }; delete next[asset.id]; return next; });
            return;
        }
        try {
            const rows = await getAssetHistory(asset.id);
            setHistory((h) => ({ ...h, [asset.id]: Array.isArray(rows) ? rows : [] }));
        } catch (error) {
            showAlert(error.message || "Не удалось загрузить историю", "error");
        }
    };

    return <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5" data-testid="employee-assets">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-bold text-slate-900">Техника и аккаунты</h2>
            {canManage && !fired ? <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setMode(mode === "new" ? null : "new")} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-700" data-testid="issue-new-asset">+ Выдать новое</button>
                <button type="button" onClick={() => (mode === "free" ? setMode(null) : openFree())} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="issue-free-asset">Выдать из свободного</button>
            </div> : null}
        </div>
        {fired ? <p className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">Сотрудник уволен: всё, что за ним числилось, при увольнении стало свободным. Историю видно в поиске по номеру на странице «Сотрудники».</p> : null}

        {mode === "new" ? <form onSubmit={issueNew} className="mb-4 flex flex-wrap items-end gap-2 rounded-xl bg-slate-50 p-3" data-testid="new-asset-form">
            <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Категория</span>
                <select value={form.category_id} onChange={(e) => setForm((v) => ({ ...v, category_id: e.target.value }))} className={inputCls} data-testid="new-asset-category">
                    <option value="">— выберите —</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></label>
            <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Инв. номер</span>
                <input type="text" maxLength={100} value={form.inventory_number} onChange={(e) => setForm((v) => ({ ...v, inventory_number: e.target.value }))} className={inputCls} data-testid="new-asset-inventory" /></label>
            <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">{identifierLabel(category?.kind)}{category?.kind === "sim" || category?.kind === "account" ? " *" : ""}</span>
                <input type="text" maxLength={200} value={form.identifier} onChange={(e) => setForm((v) => ({ ...v, identifier: e.target.value }))} className={inputCls} data-testid="new-asset-identifier" /></label>
            <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Примечание</span>
                <input type="text" maxLength={1000} value={form.note} onChange={(e) => setForm((v) => ({ ...v, note: e.target.value }))} className={inputCls} /></label>
            <button type="submit" disabled={busy} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-50" data-testid="new-asset-submit">Выдать</button>
            <button type="button" onClick={() => setNewCategory(newCategory ? null : { name: "", kind: "device" })} className="px-2 py-2 text-xs text-slate-500 underline-offset-2 hover:underline" data-testid="add-asset-category">+ категория</button>
        </form> : null}
        {mode === "new" && newCategory ? <form onSubmit={addCategory} className="-mt-2 mb-4 flex flex-wrap items-end gap-2 rounded-xl bg-slate-50 p-3" data-testid="new-category-form">
            <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Новая категория</span>
                <input type="text" maxLength={100} value={newCategory.name} onChange={(e) => setNewCategory((v) => ({ ...v, name: e.target.value }))} className={inputCls} data-testid="new-category-name" /></label>
            <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Тип</span>
                <select value={newCategory.kind} onChange={(e) => setNewCategory((v) => ({ ...v, kind: e.target.value }))} className={inputCls}>
                    {Object.entries(KIND_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select></label>
            <button type="submit" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold" data-testid="new-category-submit">Добавить</button>
        </form> : null}

        {mode === "free" ? <form onSubmit={issueFree} className="mb-4 flex flex-wrap items-end gap-2 rounded-xl bg-slate-50 p-3" data-testid="free-asset-form">
            <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Свободное</span>
                <select value={freeId} onChange={(e) => setFreeId(e.target.value)} className={`${inputCls} max-w-[360px]`} data-testid="free-asset-select">
                    <option value="">{freeItems.length ? "— выберите —" : "Свободного нет"}</option>
                    {freeItems.map((a) => <option key={a.id} value={a.id}>{assetTitle(a)}</option>)}
                </select></label>
            <button type="submit" disabled={busy || !freeId} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Выдать</button>
        </form> : null}

        {loading ? <p className="py-6 text-center text-sm text-slate-400">Загрузка…</p>
            : !items.length ? <p className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500" data-testid="employee-assets-empty">За сотрудником ничего не числится</p>
                : <ul className="divide-y divide-slate-100" data-testid="employee-assets-list">
                    {items.map((asset) => <li key={asset.id} className="py-2.5" data-testid={`employee-asset-${asset.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-slate-900">{asset.category}
                                    {asset.identifier ? <span className="ml-2 font-mono text-slate-700">{asset.identifier}</span> : null}
                                    {asset.inventory_number ? <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">инв. {asset.inventory_number}</span> : null}
                                </div>
                                <div className="text-xs text-slate-400">{KIND_LABELS[asset.kind] || ""} {sinceText(asset)}{asset.note ? ` · ${asset.note}` : ""}</div>
                            </div>
                            <div className="flex gap-3 text-xs">
                                <button type="button" onClick={() => toggleHistory(asset)} className="text-slate-500 hover:text-slate-800" data-testid={`asset-history-${asset.id}`}>История</button>
                                {canManage ? <>
                                    <button type="button" onClick={() => setMoving(moving === asset.id ? null : asset.id)} className="font-semibold text-slate-700 hover:text-amber-700" data-testid={`move-asset-${asset.id}`}>Передать</button>
                                    <button type="button" disabled={busy} onClick={() => move(asset, { type: "free" })} className="text-slate-500 hover:text-red-700" data-testid={`return-asset-${asset.id}`}>Вернуть</button>
                                </> : null}
                            </div>
                        </div>
                        {moving === asset.id ? <MoveAssetForm excludeEmployeeId={employeeId} busy={busy} onSubmit={(body) => move(asset, body)} onCancel={() => setMoving(null)} /> : null}
                        {history[asset.id] ? <div className="mt-2 rounded-lg bg-slate-50 p-2"><AssetHistoryList items={history[asset.id]} /></div> : null}
                    </li>)}
                </ul>}
    </section>;
}
